import { useState, useRef, useEffect } from "react";
import { BiChevronDown, BiChevronUp } from "react-icons/bi";
import useOutsideClick from "CustomHooks/useOutsideClick";

const PortalMonthDropdown = ({
    label,
    options = [],
    value,
    onChange,
    name,
    width = "w-28"
}) => {
    const [open, setOpen] = useState(false);
    const [dropDirection, setDropDirection] = useState("down");
    const [dropdownMaxHeight, setDropdownMaxHeight] = useState(160);
    const [selectedRowNumber, setSelectedRowNumber] = useState(0);

    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);
    useOutsideClick(dropdownRef, setOpen);

    const handleChange = (option) => {
        onChange({
            target: {
                value: option,
                name: name,
                dataset: {
                    option: option,
                },
            },
        });
    };

    const scrollToItem = (index) => {
        const elem = document.getElementById(`PortalDropdown_${index}`);
        if (elem) {
            elem.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
        }
    };

    const onKeyDownPressed = (e) => {
        if (e.key === "Tab") {
            setOpen(false)
            // Allow tab to move to next field, don't prevent default
            return;
        }

        if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
            setOpen(true);
            setSelectedRowNumber(0);
            return;
        }

        if (e.key === "Enter") {
            if (options[selectedRowNumber]) {
                handleChange(options[selectedRowNumber]);
                setOpen(!open);
            }
        } else if (e.key === "ArrowUp") {
            if (selectedRowNumber > 0) {
                setSelectedRowNumber((prev) => {
                    const newIndex = prev - 1;
                    scrollToItem(newIndex);
                    return newIndex;
                });
            }
        } else if (e.key === "ArrowDown") {
            if (selectedRowNumber < options.length - 1) {
                setSelectedRowNumber((prev) => {
                    const newIndex = prev + 1;
                    scrollToItem(newIndex);
                    return newIndex;
                });
            }
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };
    useEffect(() => {
        if (!open) return;

        const decideDirection = () => {
            if (!dropdownRef.current) return;
            const trigger = dropdownRef.current.querySelector(".dropdown-trigger");
            if (!trigger) return;

            const rect = trigger.getBoundingClientRect();
            const viewportHeight =
                window.innerHeight || document.documentElement.clientHeight;
            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;
            const defaultMenuCap = 160;

            if (spaceBelow >= defaultMenuCap) {
                setDropDirection("down");
                setDropdownMaxHeight(Math.floor(spaceBelow - 8));
            } else if (spaceAbove >= defaultMenuCap) {
                setDropDirection("up");
                setDropdownMaxHeight(Math.floor(spaceAbove - 8));
            } else {
                const useDown = spaceBelow >= spaceAbove;
                setDropDirection(useDown ? "down" : "up");
                setDropdownMaxHeight(
                    Math.floor((useDown ? spaceBelow : spaceAbove) - 8)
                );
            }
        };

        decideDirection();
        window.addEventListener("resize", decideDirection);
        window.addEventListener("scroll", decideDirection, true);

        return () => {
            window.removeEventListener("resize", decideDirection);
            window.removeEventListener("scroll", decideDirection, true);
        };
    }, [open]);

    useEffect(() => {
        if (open) {
            // Find index of current value
            const currentIndex = options.findIndex((opt) => opt === value);

            if (currentIndex !== -1) {
                setSelectedRowNumber(currentIndex);
                // Scroll the selected item into view
                setTimeout(() => scrollToItem(currentIndex), 0);
            } else {
                setSelectedRowNumber(0);
            }

            if (triggerRef.current) {
                triggerRef.current.focus();
            }
        }
    }, [open, options, value]);


    return (
        <div className="relative" ref={dropdownRef}>
            {/* Trigger */}
            <div
                ref={triggerRef}
                role="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                tabIndex={0}
                onClick={() => setOpen(!open)}
                onKeyDown={onKeyDownPressed}
                className={`dropdown-trigger peer border-2 rounded-lg outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 transition-colors duration-200 border-gray-200 hover:border-gray-300 px-2 h-10 flex justify-between items-center cursor-pointer bg-white ${width}`}
            >
                <span className={value ? "text-black" : "text-gray-400"}>
                    {value || ""}
                </span>
             {open===true? <BiChevronUp className="w-4 h-4 text-gray-500"/> : <BiChevronDown className="w-4 h-4 text-gray-500" />} 
            </div>

            {/* Label */}
            <label
                className={`absolute left-2 text-sm transition-all bg-white px-1 ${open || value ? "-top-2 text-xs" : "top-2"} ${open ? "text-indigo-600" : value ? "text-gray-600" : "text-gray-400"}`}
            >
                {label}
            </label>

            {/* Dropdown list */}
            {open && (
                <ul
                    tabIndex={-1}
                    className={`absolute focus:outline-none focus:ring-0 ${dropDirection === "up" ? "bottom-full mb-1" : "mt-1"
                        } w-full bg-white border border-gray-300 rounded shadow-md z-50 overflow-y-auto`}
                    style={{ maxHeight: dropdownMaxHeight }}
                >
                    {options.map((option, index) => (
                        <li
                            id={`PortalDropdown_${index}`}
                            key={option}
                            onClick={() => {
                                handleChange(option);
                                setOpen(!open);
                            }}
                            className={`px-3 py-1 cursor-pointer hover:bg-gray-100 
                                ${option === value ? "bg-gray-200 font-semibold" : ""} 
                                ${index === selectedRowNumber ? "bg-indigo-100" : ""}`}
                        >
                            {option}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default PortalMonthDropdown;
